import {
  type CSSProperties,
  type FC,
  type PropsWithChildren,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

import {
  AgentChatHostContext,
  type AgentChatHostProps,
  ChatWorkspaceContext,
  type ChatWorkspaceReservation,
} from '../model';

import styles from './ChatWorkspace.module.scss';
import { PageScrollbar } from './PageScrollbar';

export const ChatWorkspace: FC<PropsWithChildren> = ({ children }) => {
  const [host, setHost] = useState<AgentChatHostProps | null>(null);
  const [reservation, reserve] = useState<ChatWorkspaceReservation>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const wasPinned = useRef(false);
  const pageScrollTop = useRef(window.scrollY);
  const isPinned = reservation !== null;
  // Track the active page scroller's position so pin/unpin can move the
  // scroll offset between the root scroller and the workspace scrollArea
  // without losing the place.
  useEffect(() => {
    const scroller = scrollRef.current;
    const onScroll = (event: Event) => {
      const target =
        event.target === document ? document.documentElement : event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }
      if ((target === scroller) === wasPinned.current) {
        pageScrollTop.current = target.scrollTop;
      }
    };
    document.addEventListener('scroll', onScroll, true);
    return () => document.removeEventListener('scroll', onScroll, true);
  }, []);
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller || wasPinned.current === isPinned) {
      return;
    }
    if (isPinned) {
      scroller.scrollTop = pageScrollTop.current;
      window.scrollTo(0, 0);
    } else {
      window.scrollTo(0, pageScrollTop.current);
    }
    wasPinned.current = isPinned;
  }, [isPinned]);
  return (
    <AgentChatHostContext.Provider value={{ host, setHost }}>
      <ChatWorkspaceContext.Provider value={{ reserve }}>
        <div
          className={styles.workspace}
          data-chat-workspace
          data-pinned-side={reservation?.side}
          style={
            {
              '--chat-left': `${reservation?.side === 'left' ? reservation.width : 0}px`,
              '--chat-right': `${reservation?.side === 'right' ? reservation.width : 0}px`,
            } as CSSProperties
          }
        >
          <div ref={scrollRef} className={styles.scrollArea}>
            {children}
          </div>
        </div>
        {/* Outside the workspace div: while pinned the workspace is a
          transformed, overflow-hidden containing block — the overlay thumb
          must stay viewport-anchored and unclipped. */}
        <PageScrollbar scrollerRef={scrollRef} pinned={isPinned} />
      </ChatWorkspaceContext.Provider>
    </AgentChatHostContext.Provider>
  );
};
